import './style.scss';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation, useSearchParams, Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Menu } from 'primereact/menu';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';

// eslint-disable-next-line import/no-duplicates
import { getAllCouncilTypes } from 'apis/councils.api';
import { getAllByUser as getAllOrganizationsByUser } from 'apis/organizations.api';
import { getAll as getAllPrograms } from 'apis/programs.api';
import { getAllResearchTypes, getAllStatuses } from 'apis/projectProposals.api';
// eslint-disable-next-line import/no-duplicates
import { getAllCouncils } from 'apis/councils.api';
import { getAllResearchFieldsByUserId } from 'apis/researchFields.api';

import { Confirm } from 'features/ReviseProjectProposal/components';
import SearchFilters from 'features/CouncilPrequalification/components/SearchFilters';

import ErrorDialog from 'components/ErrorDialog';
import Pagination from 'components/Pagination';
import { PROJECT_PROPOSAL_STATUSES, COUNCIL_TYPE_CODE } from 'constant';
import RequestUpdateForm from 'features/ReviseProjectProposal/components/RequestUpdateForm';
import CreateContractForm from 'features/ReviseProjectProposal/components/CreateContractForm';
import { handleEnterKeyPress, mapError } from 'utils/func';
import { totalExpenditureSchema } from 'features/ReviseProjectProposal/validation';

function CouncilViewList() {
  // #region data
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams({ replace: true });
  const dialogConfirmRef = useRef();
  const dialogRequestUpdateRef = useRef();
  const dialogCreateContractRef = useRef();
  const approvalMenu = useRef(null);
  const [pagination, setPagination] = useState({
    first: parseInt(searchParams.get('first'), 10) || 0,
    rowsPerPage: 10,
  });
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [isVisibleSearchFilters, setIsVisibleSearchFilters] = useState(false);
  const [isLoadedFilterFromLocalStorage, setIsLoadedFilterFromLocalStorage] = useState(false);

  const [filtersTable, setFiltersTable] = useState({
    _name: { value: null },
    _researchType: { value: null },
    _researchField: { value: null },
    _councilType: { value: null },
    _program: { value: null },
    _timeCreated: { value: null },
    _reviewDeadline: { value: null },
  });

  // background filters, sync with localStorage
  const [filters, setFilters] = useState({
    name: { value: null },
    researchTypes: { value: null },
    researchFieldIds: null,
    councilTypeIds: null,
    programIds: { value: null },
    timeCreated: { start: null, end: null },
    reviewDeadline: { start: null, end: null },
  });

  // applied filters, change every time click on Apply and Clear button for update
  const [appliedFilters, setAppliedFilters] = useState({
    name: { value: null },
    researchTypes: { value: null },
    researchFieldIds: null,
    councilTypeIds: null,
    program: { value: null },
    timeCreated: { start: null, end: null },
    reviewDeadline: { start: null, end: null },
  });

  const {
    data: _data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    ['councilList', pagination, appliedFilters],
    ({ queryKey: [, _pagination, _filters] }) => getAllCouncils(_pagination, _filters),
    {
      enabled: isLoadedFilterFromLocalStorage,
    }
  );
  const data = useMemo(
    () => _data?.data ?? { data: [], pagination: { row: 10, totalRecords: 0 } },
    [_data]
  );

  const { data: organizationsData } = useQuery(
    ['projectProposalOrganizations'],
    getAllOrganizationsByUser
  );

  const organizations = useMemo(
    () =>
      organizationsData?.data?.map((e) => ({
        id: e?.id,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [organizationsData, t]
  );

  const { data: researchFieldsData } = useQuery(
    ['projectProposalResearchFieldsByUserId'],
    getAllResearchFieldsByUserId
  );
  const researchFields = useMemo(
    () =>
      researchFieldsData?.data?.map((e) => ({
        id: e.id,
        name: e?.code ? t(`researchFields.${e.code}`) : e.name,
      })) ?? [],
    [researchFieldsData, t]
  );

  const { data: researchTypesData } = useQuery(
    ['projectProposalResearchTypes'],
    getAllResearchTypes
  );
  const researchTypes = useMemo(
    () =>
      researchTypesData?.data?.map((e) => ({
        id: e.id,
        name: e?.code ? t(`researchTypes.${e.code}`) : e.name,
      })) ?? [],
    [researchTypesData, t]
  );

  const { data: programData } = useQuery(['program'], getAllPrograms);
  const programs = useMemo(() => programData?.data ?? [], [programData]);

  const { data: councilTypesData } = useQuery(['councilTypes'], getAllCouncilTypes);
  const councilTypes = useMemo(() => {
    if (councilTypesData?.data?.length > 0) {
      return councilTypesData?.data?.map((item) => ({
        id: item?.id,
        name: item?.code ? t(`council.viewList.${item.code}`) : item?.name,
      }));
    }
    return [];
  }, [councilTypesData, t]);

  const { data: statusesData } = useQuery(['projectProposalStatuses'], getAllStatuses);
  const statuses = useMemo(
    () =>
      statusesData?.data?.map((e) => ({
        id: e.id,
        code: e?.name,
        name: e?.name ? t(`statuses.${e.name}`) : '',
      })) ?? [],
    [statusesData, t]
  );

  const approvalItems = [
    {
      label: t('projectProposal.viewList.approve'),
      icon: 'pi pi-check',
      command: () => {},
    },
    { separator: true },
    {
      label: t('projectProposal.viewList.changeRequest'),
      icon: 'pi pi-refresh',
      command: () => {},
    },
  ];

  // #endregion Data

  // #region Event
  useEffect(() => {
    const localFilters = JSON.parse(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-filters`) ?? '{}'
    );

    const modifySubmittedAt = {};
    if (localFilters?.submittedAt) {
      const { start, end } = localFilters.submittedAt;
      if (start || end) {
        modifySubmittedAt.start = start ? new Date(start) : '';
        modifySubmittedAt.end = end ? new Date(end) : '';
      }
    }

    setFilters({
      ...localFilters,
      submittedAt: modifySubmittedAt,
      totalExpenditure: { min: undefined, max: undefined },
      statusName: [PROJECT_PROPOSAL_STATUSES.SUBMIT, PROJECT_PROPOSAL_STATUSES.APPROVED],
    });
    setAppliedFilters({
      ...localFilters,
      submittedAt: modifySubmittedAt,
      totalExpenditure: { min: undefined, max: undefined },
      statusName: [PROJECT_PROPOSAL_STATUSES.SUBMIT, PROJECT_PROPOSAL_STATUSES.APPROVED],
    });

    // Set rows per page in localStorage
    const localRowsPerPage = parseInt(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-rowsPerPage`),
      10
    );
    if (localRowsPerPage) {
      setPagination((cur) => ({ ...cur, rowsPerPage: localRowsPerPage }));
    }

    // Set current page (first) with searchParams
    const currentFirst = parseInt(searchParams.get('first'), 10) || 0;
    setPagination((cur) => ({ ...cur, first: currentFirst }));
    setIsLoadedFilterFromLocalStorage(true);
  }, []);

  // #endregion Event

  // #region Render
  const isRowSelectable = (event) => event.data.deletedAt === null;

  // Hàm định dạng ngày
  function formatDate(isoString) {
    const date = new Date(isoString);
    const day = `0${date.getDate()}`.slice(-2);
    const month = `0${date.getMonth() + 1}`.slice(-2);
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const formatName = (value) => {
    if (value?.councilType?.code === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) {
      return (
        <Link
          to={`/projects-revise/${value?.projectProposalCouncils?.[0]?.projectProposal?.id}?tab=2`}
          className="hover:underline"
          state={{
            template: value?.template,
          }}
        >
          {value?.name}
        </Link>
      );
    }
    if (value?.councilType?.code === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) {
      return (
        <Link
          to={`/projects-revise/${value?.projectProposalCouncils?.[0]?.projectProposal?.id}?tab=3`}
          className="hover:underline"
        >
          {value?.name}
        </Link>
      );
    }
    if (value?.councilType?.code === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL) {
      return (
        <Link
          to={`/projects-submit/${value?.summaryReport?.projectId}/${value?.projectProposalCouncils?.[0]?.projectProposal?.id}/${value?.summaryReport?.id}/midterm-document?tab=2`}
          className="hover:underline"
        >
          {value?.name}
        </Link>
      );
    }
    if (value?.councilType?.code === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
      return (
        <Link
          to={`/projects-submit/${value?.summaryReport?.projectId}/${value?.projectProposalCouncils?.[0]?.projectProposal?.id}/${value?.summaryReport?.id}/acceptance-document?tab=2`}
          className="hover:underline"
        >
          {value?.name}
        </Link>
      );
    }
    return (
      <Link to={`/councils/${value?.id}?`} className="hover:underline">
        {value?.name}
      </Link>
    );
  };

  const formatCouncilType = (value) =>
    value?.councilType?.id ? t(`councilTypes.${value?.councilType?.code}`) : '';

  const formatResearchFields = (value) => (
    <div>
      {value?.projectProposalResearchFields
        ?.sort((a, b) => {
          if (a.priority === null) return 1;
          if (b.priority === null) return -1;
          if (a.priority === b) return 0;
          return a.priority < b.priority ? -1 : 1;
        })
        ?.map((researchField, index) => (
          <p key={researchField.researchField.id} className="text-left m-0">
            {`(${researchField?.priority ?? index + 1}) ${t(
              `researchFields.${researchField.researchField.code}`
            )}`}
          </p>
        ))}
    </div>
  );

  const handleKeyDown = useCallback(
    (e) => {
      handleEnterKeyPress(e, () => {
        setAppliedFilters(filters);
      });
    },
    [filters]
  );

  // Filter name
  const councilNameFilter = () => (
    <InputText
      value={filters.name}
      onChange={(e) => {
        const newFilters = { ...filters, name: e.target.value };
        setFilters(newFilters);
      }}
      onKeyDown={handleKeyDown}
      placeholder={t('councilList.councilName')}
      className="p-column-filter"
    />
  );

  const programFilter = () => (
    <MultiSelect
      value={filters.programIds}
      onChange={(e) => {
        const newFilters = { ...filters, programIds: e.value };
        setFilters(newFilters);
      }}
      options={programs}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('projectProposal.viewList.program')}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
      panelStyle={{ maxWidth: '680px' }}
    />
  );

  const councilTypeFilter = () => (
    <MultiSelect
      value={filters.councilTypeIds}
      onChange={(e) => {
        const newFilters = { ...filters, councilTypeIds: e.value };
        setFilters(newFilters);
      }}
      options={councilTypes}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('projectProposal.viewList.councilType')}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );

  const researchFieldFilter = () => (
    <MultiSelect
      value={filters.researchFieldIds}
      onChange={(e) => {
        const newFilters = { ...filters, researchFieldIds: e.value };
        setFilters(newFilters);
      }}
      options={researchFields}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('projectProposal.viewList.researchField')}
      maxSelectedLabels={3}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );

  const formatTimeCreated = (value) => formatDate(value?.timeCreated);

  const timeCreatedFilter = () => (
    <div>
      <div className="flex align-items-center mb-2">
        <span className="w-2">{t('projectProposal.viewList.from')}</span>
        <Calendar
          showIcon
          showButtonBar
          value={filters?.timeCreated?.start ?? undefined}
          onChange={(e) => {
            const newFilters = {
              ...filters,
              timeCreated: { ...filters.timeCreated, start: e.value },
            };
            setFilters(newFilters);
          }}
          dateFormat="dd/mm/yy"
          locale={t('lang') !== 'lang' ? t('lang') : 'vi'}
        />
      </div>
      <div className="flex align-items-center">
        <span className="w-2">{t('projectProposal.viewList.to')}</span>
        <Calendar
          showIcon
          showButtonBar
          value={filters?.timeCreated?.end ?? undefined}
          onChange={(e) => {
            const newFilters = {
              ...filters,
              timeCreated: { ...filters.timeCreated, end: e.value },
            };
            setFilters(newFilters);
          }}
          dateFormat="dd/mm/yy"
          locale={t('lang') !== 'lang' ? t('lang') : 'vi'}
        />
      </div>
    </div>
  );

  const formatReviewDeadline = (value) => formatDate(value?.reviewDeadline);

  const reviewDeadlineFilter = () => (
    <div>
      <div className="flex align-items-center mb-2">
        <span className="w-2">{t('projectProposal.viewList.from')}</span>
        <Calendar
          showIcon
          showButtonBar
          value={filters?.reviewDeadline?.start ?? undefined}
          onChange={(e) => {
            const newFilters = {
              ...filters,
              reviewDeadline: { ...filters.reviewDeadline, start: e.value },
            };
            setFilters(newFilters);
          }}
          dateFormat="dd/mm/yy"
          locale={t('lang') !== 'lang' ? t('lang') : 'vi'}
        />
      </div>
      <div className="flex align-items-center">
        <span className="w-2">{t('projectProposal.viewList.to')}</span>
        <Calendar
          showIcon
          showButtonBar
          value={filters?.reviewDeadline?.end ?? undefined}
          onChange={(e) => {
            const newFilters = {
              ...filters,
              reviewDeadline: { ...filters.reviewDeadline, end: e.value },
            };
            setFilters(newFilters);
          }}
          dateFormat="dd/mm/yy"
          locale={t('lang') !== 'lang' ? t('lang') : 'vi'}
        />
      </div>
    </div>
  );

  const filterClearTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.clear')}
      onClick={() => {
        options.filterClearCallback();
      }}
      severity="secondary"
      outlined
    />
  );

  const filterApplyTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.apply')}
      onClick={() => {
        options.filterApplyCallback();
        localStorage.setItem(
          `admin-${location.pathname.slice(1)}-filters`,
          JSON.stringify(filters)
        );
      }}
      disabled={
        !totalExpenditureSchema.isValidSync({
          min: filters?.totalExpenditure?.min ?? undefined,
          max: filters?.totalExpenditure?.max ?? undefined,
        })
      }
      severity="success"
    />
  );

  const filterActiveStyle = (field) =>
    filters[field] && (filters[field]?.length > 0 || Object.values(filters[field]).some((e) => e))
      ? { color: '#22C55E' }
      : { color: '#6c757d' };

  const renderHeader = () => (
    <div className="flex justify-content-between">
      <Button
        type="button"
        icon="pi pi-filter"
        label={t('projectProposal.viewList.searchFilters')}
        outlined
        onClick={() => setIsVisibleSearchFilters(true)}
      />
    </div>
  );

  if (isError) {
    return <h4 className="text-red-500">{mapError(t, error)}</h4>;
  }

  return (
    <div className="card relative">
      {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}
      <div className="flex align-items-center justify-content-between gap-2 pb-4">
        <h4 className="m-0 hidden sm:block text-xl sm:text-2xl">{t('councilList.title')}</h4>
        <Link to="/councils/create">
          <Button
            className="bg-cyan-600 border-cyan-600"
            icon="pi pi-plus"
            label={t('councilList.addCouncil')}
          />
        </Link>
      </div>

      <DataTable
        value={data?.data}
        className="council-list"
        dataKey="id"
        header={renderHeader}
        showGridlines
        scrollable
        selectionMode="multiple"
        showSelectAll={false}
        isDataSelectable={isRowSelectable}
        filters={filtersTable}
        onFilter={(e) => setFiltersTable(e.filters)}
        filterDisplay="menu"
        loading={isLoading}
        emptyMessage={t('projectProposal.viewList.emptyMessage')}
      >
        <Column
          header={t('councilList.councilName')}
          headerStyle={filterActiveStyle('name')}
          body={formatName}
          filter
          filterField="_name"
          filterElement={councilNameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, name: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem', maxWidth: '16rem', textAlign: 'left', color: '#6366F1' }}
          frozen
          alignFrozen="center"
        />
        <Column
          field="program.name"
          header={t('councilList.program')}
          headerStyle={filterActiveStyle('programIds')}
          dataType="date"
          filter
          filterField="_program"
          filterElement={programFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, programIds: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem' }}
        />
        <Column
          header={t('councilList.councilType')}
          headerStyle={{
            ...filterActiveStyle('councilTypeIds'),
            textAlign: 'center',
          }}
          body={formatCouncilType}
          filter
          filterField="_councilType"
          filterElement={councilTypeFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = {
              ...filters,
              councilTypeIds: [],
            };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '10rem', maxWidth: '16rem' }}
        />
        <Column
          header={t('councilList.timeCreated')}
          headerStyle={filterActiveStyle('timeCreated')}
          body={formatTimeCreated}
          dataType="date"
          filter
          filterField="_timeCreated"
          filterElement={timeCreatedFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = {
              ...filters,
              timeCreated: {},
            };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '4rem' }}
        />
        <Column
          header={t('councilList.reviewDeadline')}
          headerStyle={filterActiveStyle('reviewDeadline')}
          body={formatReviewDeadline}
          dataType="date"
          filter
          filterField="_reviewDeadline"
          filterElement={reviewDeadlineFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = {
              ...filters,
              reviewDeadline: {},
            };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '4rem' }}
        />
        <Column
          field="projectProposalCouncils.projectProposal.projectProposalResearchFields"
          header={t('councilList.researchField')}
          // headerStyle={{ ...filterActiveStyle('researchFieldId'), justifyContent: 'center' }}
          headerStyle={filterActiveStyle('researchFieldIds')}
          body={formatResearchFields}
          filter
          filterField="_researchField"
          filterElement={researchFieldFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, researchFieldIds: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '10rem', textAlign: 'left' }}
        />
      </DataTable>

      <Pagination
        pagination={pagination}
        setPagination={setPagination}
        paginationData={data.pagination}
        pageName="councils"
      />

      {isVisibleSearchFilters && (
        <SearchFilters
          visible={isVisibleSearchFilters}
          setVisible={setIsVisibleSearchFilters}
          filters={filters}
          setFilters={setFilters}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          organizations={organizations}
          researchFields={researchFields}
          researchTypes={researchTypes}
          programs={programs}
          councilTypes={councilTypes}
          statuses={statuses}
        />
      )}

      <Menu model={approvalItems} popup ref={approvalMenu} />
      <Confirm ref={dialogConfirmRef} refetch={refetch} />
      <RequestUpdateForm ref={dialogRequestUpdateRef} />

      <CreateContractForm ref={dialogCreateContractRef} />

      <ErrorDialog
        title={t('projectProposal.exportExcelErrorTitle')}
        visible={isErrorDialogOpen}
        setVisible={setIsErrorDialogOpen}
      />
      <ErrorDialog
        title={t('projectProposal.downloadZipErrorTitle')}
        visible={isErrorDialogOpen}
        setVisible={setIsErrorDialogOpen}
      />
      <ErrorDialog
        title={t('projectProposal.sendRemindEmailErrorTitle')}
        visible={isErrorDialogOpen}
        setVisible={setIsErrorDialogOpen}
      />
    </div>
  );
  // #endregion Render
}

export default CouncilViewList;
